'use client';

import { useState, useEffect, useCallback } from 'react';

const POLL_INTERVAL_MS = 30_000; // 30 seconds

export interface DashboardPollingState {
  /** Increments each poll cycle — components use this as a dependency to re-derive data */
  tick: number;
  /** ISO timestamp of the last successful refresh */
  lastUpdatedAt: Date;
  /** Human-readable "X min ago" / "just now" label */
  lastUpdatedLabel: string;
  /** Whether a refresh is in flight */
  isRefreshing: boolean;
  /** Manually trigger an immediate refresh */
  refresh: () => void;
}

function getRelativeLabel(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin === 1) return '1 min ago';
  return `${diffMin} min ago`;
}

export function useDashboardPolling(): DashboardPollingState {
  const [tick, setTick] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>(() => new Date());
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState('just now');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const doRefresh = useCallback(() => {
    setIsRefreshing(true);
    // Simulate a brief async fetch (replace with real API calls when backend is ready)
    setTimeout(() => {
      setTick((t) => t + 1);
      const now = new Date();
      setLastUpdatedAt(now);
      setLastUpdatedLabel('just now');
      setIsRefreshing(false);
    }, 400);
  }, []);

  // Auto-poll every 30 seconds
  useEffect(() => {
    const id = setInterval(doRefresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [doRefresh]);

  // Update the relative label every 10 seconds so "just now" → "30s ago" etc.
  useEffect(() => {
    const id = setInterval(() => {
      setLastUpdatedLabel(getRelativeLabel(lastUpdatedAt));
    }, 10_000);
    return () => clearInterval(id);
  }, [lastUpdatedAt]);

  return { tick, lastUpdatedAt, lastUpdatedLabel, isRefreshing, refresh: doRefresh };
}
